import React, { useCallback, useState } from 'react';
import { useFontContext } from '../context/FontContext.jsx';
import { useFontInjector } from '../hooks/useFontInjector.js';
import { downloadFontAsZip } from '../utils/downloadManager.js';

/**
 * A single specimen row: the type foundry convention of a small label
 * column (name + format) alongside a large live-rendered sample of the
 * actual typeface. The @font-face rule for this font is only injected
 * once this card mounts (via useFontInjector), which is what keeps
 * scrolling through a huge library cheap.
 */
export default function FontCard({ font, height }) {
  const { customText, fontSize } = useFontContext();
  const status = useFontInjector(font);
  const [downloadState, setDownloadState] = useState('idle'); // idle | working | error
  const [downloadError, setDownloadError] = useState(null);

  const handleDownload = useCallback(async () => {
    setDownloadState('working');
    setDownloadError(null);
    try {
      await downloadFontAsZip(font);
      setDownloadState('idle');
    } catch (err) {
      console.error(err);
      setDownloadState('error');
      setDownloadError(err.message || 'Не удалось скачать.');
    }
  }, [font]);

  const previewText = customText.trim().length > 0 ? customText : font.name;

  return (
    <div
      style={{ height }}
      className="group flex items-center gap-6 border-b border-ink/10 px-5 transition-colors
                 duration-200 ease-specimen hover:bg-ink/[0.03] dark:border-paper/10
                 dark:hover:bg-paper/[0.03]"
    >
      {/* Label column */}
      <div className="flex w-44 shrink-0 flex-col gap-1.5 sm:w-56">
        <span className="truncate font-sans text-sm font-medium text-ink dark:text-paper">
          {font.name}
        </span>
        <span className="flex items-center gap-2 font-mono text-[11px] text-ink/50 dark:text-paper/50">
          <span className="rounded-sm border border-current px-1 py-0.5 leading-none">
            {font.format}
          </span>
          {status === 'loading' && <span>loading…</span>}
          {status === 'error' && <span className="text-red-600 dark:text-red-400">ошибка загрузки</span>}
        </span>

        <button
          type="button"
          onClick={handleDownload}
          disabled={downloadState === 'working'}
          className="mt-1 w-fit font-mono text-[11px] text-forest underline decoration-forest/40
                     underline-offset-2 transition-colors hover:decoration-forest disabled:opacity-50
                     dark:text-amber dark:decoration-amber/40 dark:hover:decoration-amber"
        >
          {downloadState === 'working' ? 'Сборка архива...' : 'Скачать .zip'}
        </button>
        {downloadState === 'error' && (
          <span role="alert" className="font-mono text-[11px] text-red-600 dark:text-red-400">
            {downloadError}
          </span>
        )}
      </div>

      {/* Specimen text */}
      <div className="min-w-0 flex-1 overflow-hidden">
        <p
          className="truncate leading-[1.15] text-ink transition-opacity duration-300 dark:text-paper"
          style={{
            fontFamily:
              status === 'loaded' ? `"${font.family}"` : 'inherit',
            fontSize: `${fontSize}px`,
            opacity: status === 'loaded' ? 1 : 0.35,
          }}
          title={previewText}
        >
          {previewText}
        </p>
      </div>
    </div>
  );
}
